import type {
    PublicKeyCreationOptionsJSON,
    SerializedRegistrationCredential,
} from '@/lib/api/tradingSession';

import { base64UrlToArrayBuffer, bytesToBase64Url } from './webauthn';

export type SerializedAttestationCredential = SerializedRegistrationCredential;

export function parseCreationOptions(
    options: PublicKeyCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
    return {
        ...options,
        challenge: base64UrlToArrayBuffer(options.challenge),
        user: {
            ...options.user,
            id: base64UrlToArrayBuffer(options.user.id),
        },
        excludeCredentials: options.excludeCredentials?.map((credential) => ({
            id: base64UrlToArrayBuffer(credential.id),
            type: credential.type,
            transports: credential.transports as
                | AuthenticatorTransport[]
                | undefined,
        })),
    };
}

export function serializeAttestationCredential(
    credential: PublicKeyCredential,
    _label?: string,
): SerializedAttestationCredential {
    const response = credential.response as AuthenticatorAttestationResponse;
    const transports = response.getTransports?.();
    return {
        id: credential.id,
        rawId: bytesToBase64Url(new Uint8Array(credential.rawId)),
        type: 'public-key',
        authenticatorAttachment: credential.authenticatorAttachment,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {
            clientDataJSON: bytesToBase64Url(
                new Uint8Array(response.clientDataJSON),
            ),
            attestationObject: bytesToBase64Url(
                new Uint8Array(response.attestationObject),
            ),
            ...(transports?.length ? { transports } : {}),
        },
    };
}

export async function createPasskey(
    options: PublicKeyCreationOptionsJSON,
    label?: string,
): Promise<SerializedAttestationCredential> {
    const credential = await navigator.credentials.create({
        publicKey: parseCreationOptions(options),
    });
    if (!(credential instanceof PublicKeyCredential)) {
        throw new Error('No passkey credential was created.');
    }
    return serializeAttestationCredential(credential, label);
}
